import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { getCookie } from 'functions/getCookie';
import { getConfig } from 'functions/getConfig';
import { useLanguageVersions } from 'hooks/useConfig';
import { useLoadFreecodeContest } from 'hooks/useFreecode';
import { FreecodeLoading } from 'components/FreecodeLoading/FreecodeLoading';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContext } from 'hooks/useContext';
import FreecodeEditorComponents from 'components/FreecodePage/FreecodeEditorComponents';
import PageUnderConstruction from 'components/FreecodePage/PageUnderConstruction';
import { identifiers } from 'components/FreecodePage/FreecodeLayoutConstants';
import FreecodeDiscussion from 'components/FreecodeContent/FreecodeDiscussion/FreecodeDiscussion';
import SubmitChallengeModal from 'components/Modals/FreecodeModals/SubmitChallengeModal';

const HEADER_LINKS = [{ link: '/challenges', label: 'All Challenges' }];

export default function FreecodeChallenge() {
  const { contestId } = useParams();

  const [loadingDone, setLoadingDone] = useState(false);
  const [loadingError, setLoadingError] = useState(undefined);
  const [headerLinks, setHeaderLinks] = useState(HEADER_LINKS);
  const [challengeComponents, setChallengeComponents] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { setTest, setTestImpl, setLanguageData, setLanguage, setCode, setInput, setSubmit, setSponsor } =
    useFreecodeContext();
  const contestRef = useRef(undefined);

  const [languageVersions /** ignore next */, , languageVersionsError] = useLanguageVersions();
  const [freecodeResponse /** ignore next */, , freecodeError] = useLoadFreecodeContest(contestId);

  useEffect(() => {
    setSubmit(() => () => setSubmitting(true));
  }, [setSubmit]);

  useEffect(() => {
    if (!contestId) return;
    setHeaderLinks([
      ...HEADER_LINKS,
      { link: `${getConfig().REDIRECTION_ROOT_URL}/challenge/${contestId}`, label: 'Old Editor' },
    ]);
    setChallengeComponents((e) => ({
      ...e,
      [identifiers.discussion]: <FreecodeDiscussion contestId={contestId} />,
    }));
  }, [contestId]);

  useEffect(() => {
    if (!languageVersions || !freecodeResponse) return;
    // When loading is done destructure the data
    try {
      const { test, testImpl, contest, input, sponsor } = freecodeResponse;
      let allowedLanguages = languageVersions[testImpl.executionVersion];

      const foundLanguage =
        input?.language ||
        localStorage.getItem(`fsEditor-${test.category}-${test._id}-language`) ||
        (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);

      const foundCode =
        input?.code ||
        localStorage.getItem(`fsEditor-${test.category}-${test._id}-${foundLanguage}`) ||
        testImpl.generated?.skeletons?.[foundLanguage] ||
        'Code not found...';

      localStorage.setItem(`fsEditor-${test.category}-${test._id}-${foundLanguage}`, foundCode);

      setInput(input);
      setTest(test);
      setTestImpl(testImpl);
      setLanguageData(allowedLanguages);
      setLanguage(foundLanguage);
      setCode(foundCode);
      if (sponsor && (sponsor.type === 'contestSponsor' || !getCookie('isTicketUser'))) setSponsor(sponsor);
      contestRef.value = contest;
      setLoadingDone(true);
    } catch (error) {
      setLoadingError('Could not unpack freecode data');
    }
  }, [
    languageVersions,
    freecodeResponse,
    setLanguageData,
    setLanguage,
    setCode,
    setInput,
    setTest,
    setTestImpl,
    setSponsor,
  ]);

  return (
    <FreecodePage headerLinks={headerLinks} contest={contestRef.value}>
      <FreecodeLoading
        loading={!loadingDone}
        errors={[languageVersionsError, freecodeError, loadingError]}
        errorLinkText="All Challenges"
        errorLink="/challenges"
      />
      <PageUnderConstruction />
      {loadingDone && <FreecodeEditorComponents parentComponents={challengeComponents} />}
      {submitting && <SubmitChallengeModal onCancel={() => setSubmitting(false)} contest={contestRef.value} />}
    </FreecodePage>
  );
}
